/**
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/storageLocation';

import properties from '../properties/locations';

class StorageLocations extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {object} */
	get xhrTrackProgress() {
		return {
			getLocations: true,
		};
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getLocations: 'Fetching storage locations',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getLocations: 'Fetched storage locations',
		};
	}

	/**
	 * @property {object}           methods                  - The methods available on this model
	 * @property {function|array}   methods.read             - The read method (get)
	 * @property {function|array}   methods.getLocationTypes - Get location types (when called will update app/properties/locations)
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getLocations', '', false];
			},
			getLocationTypes: ['getLocationTypes'],
		};
	}

	/**
	 * Parsers for this model
	 * @type {object}
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} data Data
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				return _.map(data, (loc) =>
					_.extend(loc, {
						label: i18n.t('i18n.' + _.toCamelCase(loc.name), {
							defaultValue: loc.name,
						}),
						locationTypeLabel: i18n.t(
							'i18n.' + _.toCamelCase(loc.locationTypeName),
							{defaultValue: loc.locationTypeName},
						),
					}),
				);
			},
			/**
			 * Sets the properties file location type data app/properties/locations
			 * @function
			 * @param {array} data
			 * @param {object} opts Request options
			 */
			getLocationTypes(data, opts) {
				const typeIds = properties.typeIds;
				const typeLabels = properties.typeLabels;
				_.each(data, (locationType, idx) => {
					const id = locationType.id;
					const name = _.toCamelCase(locationType.name);
					typeIds[name] = id;
					typeLabels[id] = i18n.t(`i18n.${name}`);
				});
				properties.types = _.invert(typeIds);
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		super(models, opts);
	}

	/**
	 * @method
	 * @return {Netx.Models.StorageLocation}
	 */
	getDefaultLocation() {
		return this.findWhere({defaultLocation: 1});
	}

	/**
	 * Get an object indexed by id with label values by locationTypeId
	 * Used a lot for select element options
	 * @method
	 * @param {number} locationTypeId Location type
	 * @return {object} Labels
	 */
	getLocationLabelsForType(locationTypeId) {
		locationTypeId = parseInt(locationTypeId);

		const locations = this.where({locationTypeId: locationTypeId});
		const list = {};

		_.each(locations, (locModel, idx) => {
			list[locModel.id] = locModel.get('name');
		});

		return list;
	}

	/**
	 * Returns locations that are available to be uploaded to.
	 * @method
	 * @return {Netx.Collection} Collection of active location models
	 */
	getLocationsForUpload() {
		return new Base(this.where({active: 1}));
	}

	/**
	 * Set the default storage location
	 * @method
	 * @param {Netx.Models.StorageLocation} model Sotrage location
	 * @param {object} opts Options
	 */
	setDefaultLocation(model, opts) {
		// Get current default
		const currentDefaultLocation = this.findWhere({defaultLocation: 1});

		// If not already default
		if (currentDefaultLocation === model) {
			return new $.Deferred().resolve();
		}

		// Set the new default location
		return model
			.save(
				{
					defaultLocation: 1,
					active: 1,
				},
				_.extend({wait: true}, opts),
			)
			.then(() => {
				currentDefaultLocation.set({defaultLocation: 0});
				// Trigger custom event since we will be changing the values of `defaultLocation` quite a bit here
				this.trigger('nx:change:defaultLocation', this, model);
			});
	}

	/** @type {string} */
	static get __name__() {
		return 'StorageLocations';
	}
}

export default StorageLocations;
